import { ReactNode } from "react";
import { ChartTooltipContent, GappedChart, cn } from "web-design-system";
import { ExternalHoldings, PortfolioReviewBlock } from "creso-core/types";
import { convertToCurrency, formatAsPercentage } from "creso-core/utils";
import { AmcLogo } from "@core/components/logo/amc-logo";
import { ReviewBlockState, ReviewSection } from "./review-section";

const formatPct = (value: string | number) => formatAsPercentage(value, "-", 0);

export function ExternalHoldingsSection({
  block,
  isLoading,
}: {
  block?: PortfolioReviewBlock<ExternalHoldings>;
  isLoading: boolean;
}) {
  const holdings = block?.data;

  return (
    <ReviewSection title="External holdings">
      <ReviewBlockState
        isLoading={isLoading}
        isError={block?.ok === false}
        isEmpty={!!holdings && !Number(holdings.external_aum)}
        emptyMessage="No holdings found under other brokers."
        className="gap-8"
      >
        {holdings ? (
          <>
            <div className="flex flex-col gap-6">
              <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg bg-card px-4 py-3">
                <SummaryStat label="External AUM">
                  {convertToCurrency(holdings.external_aum)}
                </SummaryStat>
                <SummaryStat label="Regular" accent="border-chart-1">
                  {convertToCurrency(holdings.regular_value)} (
                  {formatPct(holdings.regular_pct)})
                </SummaryStat>
                <SummaryStat label="Direct" accent="border-chart-3">
                  {convertToCurrency(holdings.direct_value)} (
                  {formatPct(holdings.direct_pct)})
                </SummaryStat>
                <SummaryStat label="Funds">
                  {holdings.fund_count} funds ({holdings.amc_count} AMCs)
                </SummaryStat>
              </div>
              <GappedChart
                className="relative h-6 w-full"
                segments={[
                  {
                    key: "regular",
                    label: "Regular",
                    value: Number(holdings.regular_pct),
                    color: "var(--color-chart-1)",
                  },
                  {
                    key: "direct",
                    label: "Direct",
                    value: Number(holdings.direct_pct),
                    color: "var(--color-chart-3)",
                  },
                ]}
                tooltipContent={
                  <ChartTooltipContent
                    hideLabel
                    valueFormatter={(v) => formatPct(Number(v))}
                  />
                }
              />
              <ReviewTable
                columns={["Broker", "AUM", "Funds"]}
                rows={holdings.brokers.map((broker) => ({
                  key: broker.broker_code,
                  cells: [
                    <div key="name" className="flex flex-col gap-1">
                      <span>{broker.broker_name}</span>
                      <span className="text-xs text-muted-foreground">
                        {broker.broker_code}
                      </span>
                    </div>,
                    convertToCurrency(broker.aum),
                    broker.fund_count,
                  ],
                }))}
              />
            </div>

            {holdings.ongoing_sips.sip_count ? (
              <div className="flex flex-col gap-4">
                <h6>Ongoing external SIPs</h6>
                <div className="flex gap-12 rounded-lg bg-card px-4 py-3">
                  <SummaryStat label="Monthly SIP">
                    {convertToCurrency(holdings.ongoing_sips.monthly_amount)}
                  </SummaryStat>
                  <SummaryStat label="No. of SIPs">
                    {holdings.ongoing_sips.sip_count}
                  </SummaryStat>
                </div>
                <ReviewTable
                  columns={["Fund Name", "SIP amount"]}
                  rows={holdings.ongoing_sips.schemes.map((scheme) => ({
                    key: scheme.scheme_isin,
                    cells: [
                      <div key="name" className="flex items-center gap-3">
                        <AmcLogo amc_code={scheme.amc_morningstar_code} />
                        <div className="flex min-w-0 flex-col gap-1">
                          <span className="truncate">
                            {scheme.scheme_name ?? scheme.scheme_isin}
                          </span>
                          <span className="truncate text-xs text-muted-foreground">
                            {scheme.category}
                          </span>
                        </div>
                      </div>,
                      convertToCurrency(scheme.sip_amount),
                    ],
                  }))}
                />
              </div>
            ) : null}
          </>
        ) : null}
      </ReviewBlockState>
    </ReviewSection>
  );
}

function SummaryStat({
  label,
  accent,
  children,
}: {
  label: string;
  accent?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "flex flex-col gap-2",
        accent && `border-l-4 pl-3 ${accent}`
      )}
    >
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-semibold">{children}</span>
    </div>
  );
}

// First column is left-aligned, the rest are numeric and right-aligned.
function ReviewTable({
  columns,
  rows,
}: {
  columns: string[];
  rows: { key: string; cells: ReactNode[] }[];
}) {
  return (
    <div className="overflow-x-auto rounded-xl border border-border">
      <table className="w-full min-w-120 text-sm">
        <thead className="bg-card">
          <tr>
            {columns.map((column, index) => (
              <th
                key={column}
                className={cn(
                  "p-4 font-semibold",
                  index === 0 ? "w-90 text-left" : "text-right"
                )}
              >
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className="border-t border-border">
              {row.cells.map((cell, index) => (
                <td
                  key={columns[index]}
                  className={cn(
                    "px-4 py-3",
                    index === 0 ? "text-left" : "text-right"
                  )}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
